import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Flex, Modal, message } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom"

import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import type { RootState } from "../../../../store.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../../Branches/redux/branches.slice.ts"
import {
  buildFolderNav,
  canFileReport,
  isUnfiled,
  knownFolderNames
} from "../../helpers/reportFolders.ts"
import {
  ALL_REPORTS,
  type ReportsNav,
  navFromSearchParams,
  reportsListHref,
  sameNav
} from "../../helpers/reportsNav.ts"
import {
  createReportFolder,
  deleteReportFolder,
  fetchReportFolders,
  fetchReports,
  renameReportFolder
} from "../../redux/reports.thunks.ts"
import { ReportFolderNameModal } from "../folders/ReportFolderNameModal.tsx"
import { useInert } from "../../hooks/useInert.ts"
import { ReportsRail } from "./ReportsRail.tsx"

export interface ReportsShellOutletContext {
  activeNav: ReportsNav
  onCreateFolder: () => void
}

type FolderDialog = { mode: "create" } | { mode: "rename"; name: string } | null

const ReportsShell = ({
  results,
  fetching,
  folders,
  foldersStatus,
  isCompanyMode,
  activeBranchId,
  reportsPageScopeKey,
  getReports,
  getFolders
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id: openReportId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [folderDialog, setFolderDialog] = useState<FolderDialog>(null)

  const activeNav = navFromSearchParams(searchParams)
  // The New Report modal keeps the list (and its view) behind it; only a
  // report's own page leaves no view selected.
  const { pathname } = useLocation()
  // The folder sidebar sits behind the New Report modal too.
  const railRef = useInert<HTMLDivElement>(
    pathname.startsWith(RoutesPaths.reports.create)
  )
  const reportPageOpen =
    Boolean(openReportId) && !pathname.startsWith(RoutesPaths.reports.create)

  useEffect(() => {
    generateDocumentTitle(t("reports.title"))
  }, [t])

  useEffect(() => {
    // Search runs over this list in the page, so the sidebar's counts are
    // always what the caller can open. The branch scope (global workspace
    // branch, or Company HQ's own saved Reports filter) lives inside the thunk
    // (`reportsBranchScopeParams`) — re-run whenever either changes, which
    // also replaces every count and row from the branch left behind.
    getReports({})
  }, [getReports, isCompanyMode, activeBranchId, reportsPageScopeKey])

  useEffect(() => {
    if (foldersStatus === "idle") getFolders()
  }, [foldersStatus, getFolders])

  const folderNav = useMemo(
    () => buildFolderNav(results, folders),
    [results, folders]
  )
  const existingFolderNames = useMemo(
    () => knownFolderNames(results, folders),
    [results, folders]
  )
  const unfiledCount = useMemo(
    () => results.filter(isUnfiled).length,
    [results]
  )

  const handleNavigate = useCallback(
    (nav: ReportsNav) => navigate(reportsListHref(nav)),
    [navigate]
  )

  const openCreateFolder = useCallback(
    () => setFolderDialog({ mode: "create" }),
    []
  )

  const submitFolderName = async (name: string): Promise<string | null> => {
    if (folderDialog?.mode === "rename") {
      const action = await dispatch(
        renameReportFolder({ name: folderDialog.name, newName: name })
      )
      if (!renameReportFolder.fulfilled.match(action))
        return action.payload?.message ?? ""
      const { skipped } = action.payload
      message.success(
        skipped
          ? t("reports.folders.renamedWithSkipped", { count: skipped })
          : t("reports.folders.renamed")
      )
      if (sameNav(activeNav, { kind: "folder", name: folderDialog.name }))
        handleNavigate({ kind: "folder", name: action.payload.name })
      return null
    }
    const action = await dispatch(createReportFolder({ name }))
    if (!createReportFolder.fulfilled.match(action))
      return action.payload?.message ?? ""
    message.success(t("reports.folders.created", { name: action.payload }))
    handleNavigate({ kind: "folder", name: action.payload })
    return null
  }

  const confirmDeleteFolder = (name: string) => {
    const filed = results.filter((report) => report.folder === name)
    const movable = filed.filter(canFileReport).length
    const readOnly = filed.length - movable
    Modal.confirm({
      title: t("reports.folders.deleteConfirm.title", { name }),
      icon: <ExclamationCircleOutlined />,
      content: (
        <Flex vertical gap={8}>
          <span>
            {movable
              ? t("reports.folders.deleteConfirm.moves", { count: movable })
              : t("reports.folders.deleteConfirm.empty")}
          </span>
          {readOnly > 0 && (
            <span>
              {t("reports.folders.deleteConfirm.readOnly", {
                count: readOnly
              })}
            </span>
          )}
        </Flex>
      ),
      okText: t("reports.folders.deleteConfirm.confirm"),
      okButtonProps: { danger: true },
      cancelText: t("reports.cancel"),
      onOk: async () => {
        const action = await dispatch(deleteReportFolder({ name }))
        if (!deleteReportFolder.fulfilled.match(action)) {
          message.error(
            action.payload?.message || t("reports.folders.errors.deleteFailed")
          )
          return
        }
        message.success(t("reports.folders.deleted", { name }))
        if (sameNav(activeNav, { kind: "folder", name }))
          handleNavigate(ALL_REPORTS)
      }
    })
  }

  const context: ReportsShellOutletContext = {
    activeNav,
    onCreateFolder: openCreateFolder
  }

  return (
    <div className="flex size-full min-h-0 gap-6 overflow-hidden p-1 -m-1 max-h-[calc(100vh-88px)]">
      <div className="flex min-h-0" ref={railRef}>
        <ReportsRail
          // A report's own page highlights no view: it was opened from one, but
          // the URL no longer says which.
          activeNav={reportPageOpen ? null : activeNav}
          folders={folderNav}
          unfiledCount={unfiledCount}
          loading={fetching || foldersStatus === "loading"}
          onNavigate={handleNavigate}
          onCreateFolder={openCreateFolder}
          onRenameFolder={(name) => setFolderDialog({ mode: "rename", name })}
          onDeleteFolder={confirmDeleteFolder}
        />
      </div>
      <div className="min-w-0 flex-1 overflow-auto">
        <Outlet context={context} />
      </div>

      {folderDialog && (
        <ReportFolderNameModal
          mode={folderDialog.mode}
          current={
            folderDialog.mode === "rename" ? folderDialog.name : undefined
          }
          existing={existingFolderNames}
          onSubmit={submitFolderName}
          onClose={() => setFolderDialog(null)}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  results: state.reports.results,
  fetching: state.reports.fetching,
  folders: state.reports.folders,
  foldersStatus: state.reports.foldersStatus,
  isCompanyMode: selectIsCompanyMode(state),
  activeBranchId: selectActiveBranchId(state),
  reportsPageScopeKey: JSON.stringify(selectPageFilter("reports")(state))
})

const mapDispatchToProps = {
  getReports: fetchReports,
  getFolders: fetchReportFolders
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportsShell)
